// Shared pure accessors for AR image objects.
//
// The API/Redux shape is inconsistent: some fields live at the top level and
// some under `metadata` (see ImageCarouselDialog + arImageSlice). These helpers
// centralize that fallback logic so the table and the zip exporter agree.

export const coerceDate = (ts) => {
  if (!ts) return null;
  // API timestamps are UTC but often lack a timezone marker.
  const needsZ =
    typeof ts === 'string' && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(ts);
  const date = new Date(needsZ ? `${ts}Z` : ts);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const getTimeStampDate = (img) =>
  coerceDate(img?.metadata?.timeStamp ?? img?.timeStamp);

export const getTimeStampMs = (img) => {
  const d = getTimeStampDate(img);
  return d ? d.getTime() : 0;
};

export const getPhotographer = (img) =>
  img?.metadata?.createdByEmail ?? img?.createdByEmail ?? '';

export const getLocationId = (img) =>
  img?.metadata?.locationId ?? img?.locationId ?? null;

export const getDeviceType = (img) =>
  img?.metadata?.deviceType ?? img?.deviceType ?? '';

export const getProcoreId = (img) =>
  img?.procoreImageId ?? img?.metadata?.procoreImageId ?? null;

export const getAugmentedUrl = (img) => img?.augmentedUrl ?? '';

// Site-space position of where the photo was taken. The AR app stores it under
// `metadata.sitePosition{X,Y,Z}` (see ImageCarouselDialog). Returns null unless
// all three components are finite numbers — callers must treat null as "no
// position", never as the origin.
export const getSitePosition = (img) => {
  const x = img?.metadata?.sitePositionX ?? img?.sitePositionX;
  const y = img?.metadata?.sitePositionY ?? img?.sitePositionY;
  const z = img?.metadata?.sitePositionZ ?? img?.sitePositionZ;
  if ([x, y, z].every((v) => Number.isFinite(v))) {
    return { x, y, z };
  }
  return null;
};

// Mean of the site positions of the supplied images, ignoring any without a
// position. Returns null when none of the images carry a position, so callers
// can fall back to leaving the value at its default.
export const getMeanSitePosition = (images = []) => {
  const positions = images.map(getSitePosition).filter(Boolean);
  if (positions.length === 0) return null;
  const sum = positions.reduce(
    (acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y, z: acc.z + p.z }),
    { x: 0, y: 0, z: 0 },
  );
  return {
    x: sum.x / positions.length,
    y: sum.y / positions.length,
    z: sum.z / positions.length,
  };
};

// Geographic position (lat/lon/elevation) of where the photo was taken, stored
// under `metadata.{latitude,longitude,elevation}` (see ImageCarouselDialog).
// Returns null unless latitude and longitude are finite. An all-zero fix is
// treated as "unknown" (the AR app's sentinel for no GPS lock — see
// ImageCarouselDialog's allZero check) and also returns null, so callers never
// mistake it for a real position on the equator. Elevation defaults to 0 when
// absent but lat/lon are present.
export const getGeoPosition = (img) => {
  const latitude = img?.metadata?.latitude ?? img?.latitude;
  const longitude = img?.metadata?.longitude ?? img?.longitude;
  const elevationRaw = img?.metadata?.elevation ?? img?.elevation;
  if (![latitude, longitude].every(Number.isFinite)) return null;
  const elevation = Number.isFinite(elevationRaw) ? elevationRaw : 0;
  if (latitude === 0 && longitude === 0 && elevation === 0) return null;
  return { latitude, longitude, elevation };
};

// Mean of the geographic positions of the supplied images, ignoring any without
// a usable fix. Returns null when none of the images carry one, so callers can
// fall back to leaving the value at its default.
export const getMeanGeoPosition = (images = []) => {
  const positions = images.map(getGeoPosition).filter(Boolean);
  if (positions.length === 0) return null;
  const sum = positions.reduce(
    (acc, p) => ({
      latitude: acc.latitude + p.latitude,
      longitude: acc.longitude + p.longitude,
      elevation: acc.elevation + p.elevation,
    }),
    { latitude: 0, longitude: 0, elevation: 0 },
  );
  return {
    latitude: sum.latitude / positions.length,
    longitude: sum.longitude / positions.length,
    elevation: sum.elevation / positions.length,
  };
};
